import { FC, Fragment, useState } from "react";
import { TextField } from "@mui/material";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { JiraTicketForm, JiraTicketFormProps } from "./JiraTicketForm";
import { JiraConnectAction } from "../JiraConnectAction";
import { jiraConnectionValidationSchema } from "../../../../../utils/validation";
import { usePostUsersMeJiraIntegrationsProjectsMutation } from "../../../../../../../../services/cloudchipr.api";
import { jiraProjectsOnCreateFixedCacheKey } from "../../../../../utils/constants/fixedCacheKeys";

export const JiraForm: FC<JiraTicketFormProps> = ({
  url,
  integrationId,
  api_token,
  jira_email,
  setFieldValue,
  ...ticketFormProps
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [fetchProjects, { isLoading }] =
    usePostUsersMeJiraIntegrationsProjectsMutation({
      fixedCacheKey: jiraProjectsOnCreateFixedCacheKey,
    });

  const [connected, setConnected] = useState(!!integrationId);

  const formik = useFormik({
    initialValues: {
      type: "jira",
      jira_email,
      api_token,
      url,
    },
    validationSchema: jiraConnectionValidationSchema,
    onSubmit: async ({ url, jira_email, api_token }) => {
      if (!api_token || !url || !jira_email) {
        return;
      }

      const body = { api_token, url, email: jira_email };

      try {
        await fetchProjects({ body }).unwrap();

        setConnected(true);

        setFieldValue("api_token", api_token);
        setFieldValue("jira_email", jira_email);
        setFieldValue("url", url);
      } catch (e: any) {
        const message = e?.data?.message || "Something went wrong!";

        enqueueSnackbar(message, {
          variant: "snackbarAlert",
          AlertSnackBarProps: {
            severity: "error",
          },
        });
      }
    },
  });
  const { dirty, isValid, values, errors, handleChange, touched, submitForm } =
    formik;

  return (
    <Fragment>
      <TextField
        fullWidth
        disabled={connected}
        value={values.jira_email || ""}
        type="email"
        size="small"
        label="Email Address*"
        name="jira_email"
        variant="outlined"
        error={!!(touched.jira_email && errors.jira_email)}
        helperText={touched.jira_email && errors.jira_email}
        onChange={handleChange}
      />
      <TextField
        fullWidth
        disabled={connected}
        value={values.api_token || ""}
        size="small"
        variant="outlined"
        label="API Token*"
        name="api_token"
        error={!!(touched.api_token && errors.api_token)}
        helperText={touched.api_token && errors.api_token}
        onChange={handleChange}
      />
      <TextField
        fullWidth
        disabled={connected}
        value={values.url || ""}
        size="small"
        label="URL *"
        name="url"
        variant="outlined"
        error={!!(touched.url && errors.url)}
        helperText={
          (touched.url && errors.url) ||
          'Please make sure the URL starts with "https"'
        }
        onChange={handleChange}
      />

      {!integrationId && (
        <JiraConnectAction
          connected={connected}
          disabled={!dirty || !isValid}
          isLoading={isLoading}
          onConnect={submitForm}
        />
      )}

      {connected && (
        <JiraTicketForm
          url={values.url}
          integrationId={integrationId}
          api_token={values.api_token}
          jira_email={values.jira_email}
          setFieldValue={setFieldValue}
          {...ticketFormProps}
        />
      )}
    </Fragment>
  );
};
